/**
 * Human-readable label mapper for Contract Twin technical enums.
 * Central point: adding a new enum requires editing ONE map + translations.js.
 *
 * Usage:
 *   import { humanLabel } from "./ctLabels";
 *   const t = useLanguage().t;
 *   humanLabel(t, "variable", "ecb_rate")     → "Tasso BCE"
 *   humanLabel(t, "clause_type", "renewal")   → "Rinnovo"
 */

const NAMESPACE = {
  variable: "ct.var",              // ecb_rate | eur_usd | oil_brent | fixed_date | other
  trigger_variable: "ct.var",      // alias
  clause_type: "ct.clause",        // renewal | price_revision | penalty | termination | parity_clause
  contract_type: "ct.type",        // leasing | indexed_supply | saas_license | nda | other
  contract_status: "ct.cstatus",   // processed | processing | pending | error
  review_status: "ct.review",      // verified | needs_review
  risk_status: "ct.cp_risk",       // stable | watch | distressed | default | unmonitored
  alert_type: "ct.alert_type",     // counterparty_risk | market_variable
  alert_status: "ct.alert_status", // active | resolved
  reco_status: "ct.reco_status",   // active | considered | dismissed
  intent: "ct.intent",             // counterparty_risk | variable_impact | whatif | unknown
};

/**
 * Convert snake_case fallback into a Titled string (e.g. "commodity_price" → "Commodity price")
 * used only when the specific translation key is missing.
 */
function snakeToLabel(raw) {
  if (raw === null || raw === undefined) return "—";
  const s = String(raw);
  if (!s) return "—";
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function humanLabel(t, kind, value) {
  if (value === null || value === undefined || value === "") return "—";
  const ns = NAMESPACE[kind];
  if (!ns) return snakeToLabel(value);
  const key = `${ns}.${value}`;
  const translated = t(key);
  // If translation missing, `t` returns the key itself → fallback to snake→label
  if (translated === key) return snakeToLabel(value);
  return translated;
}

/**
 * Sostituisce token snake_case noti dentro un testo libero (es. body raccomandazione LLM)
 * con la loro etichetta italiana/inglese. Non-destructive: se il token non è mappato lo lascia.
 */
const RAW_TOKENS = [
  ["ecb_rate", "variable"],
  ["eur_usd", "variable"],
  ["oil_brent", "variable"],
  ["fixed_date", "variable"],
  ["price_revision", "clause_type"],
  ["parity_clause", "clause_type"],
  ["counterparty_risk", "alert_type"],
  ["market_variable", "alert_type"],
  ["needs_review", "review_status"],
];

export function humanizeText(t, text) {
  if (!text) return text;
  let out = String(text);
  for (const [token, kind] of RAW_TOKENS) {
    // sostituisci solo occorrenze intere (word boundary)
    const re = new RegExp(`\\b${token}\\b`, "g");
    if (re.test(out)) {
      out = out.replace(re, humanLabel(t, kind, token));
    }
  }
  return out;
}

export default humanLabel;
